<template>
  <div class="table-container">
    <Transition name="fade">
      <!-- TABLE (after consent) -->
      <div v-if="isAccepted" key="table">
        <v-data-table
          class="m-b-30 lifeyr-table"
          :headers="visibleHeaders"
          :items="lifeyrissjodir"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          :loading="loading"
          :no-data-text="t('no_data')"
          :loading-text="t('loading')"
          hide-default-footer
          hover
        >
          <!-- adili links -->
          <template #item.adili="{ item }">
            <a
              class="table_link"
              :href="formatHref(item.vefSlod)"
              target="_blank"
              rel="noopener"
            >
              {{ item.adili }}
              <svg class="ext" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
                />
              </svg>
            </a>
          </template>

          <!-- Rettindi columns -->
          <template #item.rettindi_one="{ item }">
            <span v-if="checkAge(0)">
              <span v-if="item.tegund === 1">{{ currency(getManadargreidslu(item, 0)) }}</span>
              <span v-else>{{ getManadargreidslu(item, 0) }}</span>
            </span>
          </template>

          <template #item.rettindi_two="{ item }">
            <span v-if="checkAge(1)">
              <span v-if="item.tegund === 1">{{ currency(getManadargreidslu(item, 1)) }}</span>
              <span v-else>{{ getManadargreidslu(item, 1) }}</span>
            </span>
          </template>

          <template #item.rettindi_three="{ item }">
            <span v-if="item.tegund === 1">{{ currency(getManadargreidslu(item, 2)) }}</span>
            <span v-else>{{ getManadargreidslu(item, 2) }}</span>
          </template>

          <template #item.tegund="{ item }">
            {{ tegundLabel(item.tegund) }}
          </template>

          <template #item.lysing="{ item }">
            {{ item?.sjodFelagi?.rettindi?.fyrirvari }}
          </template>

          <!-- ✅ summary row INSIDE the table so it aligns with columns -->
          <template #body.append>
            <tr class="summary-row">
              <td
                v-for="(col, idx) in visibleHeaders"
                :key="String(col.key)"
                :class="['sum-cell', { end: col.align === 'end' }]"
              >
                <!-- first visible column gets the label -->
                <template v-if="idx === 0">
                  <span class="label">Samtals réttindi á mánuði</span>
                </template>

                <!-- align sums to their actual columns -->
                <template v-else-if="col.key === 'rettindi_one'">
                  <span v-if="checkAge(0)">{{ currency(getSumRettindi(0)) }}</span>
                </template>

                <template v-else-if="col.key === 'rettindi_two'">
                  <span v-if="checkAge(1)">{{ currency(getSumRettindi(1)) }}</span>
                </template>

                <template v-else-if="col.key === 'rettindi_three'">
                  {{ currency(getSumRettindi(2)) }}
                </template>
                <!-- other columns remain empty -->
              </td>
            </tr>
          </template>

          <!-- footer: pagination + after text (summary moved out) -->
          <template #bottom>
            <!-- pagination bar (drives the table) -->
            <div class="table-pagination">
              <v-select
                v-model="itemsPerPage"
                :items="pageSizes"
                density="compact"
                class="rows-select"
                hide-details
                :label="t('perpage')"
              />
              <v-pagination
                v-model="page"
                :length="pageCount"
                total-visible="7"
                density="comfortable"
              />
            </div>

            <!-- explanatory text -->
            <div class="aftertext">
              <h2 class="bold_text">{{ t('subpage_rettindi_lifeyrisgatt_header1') }}</h2>
              <p>{{ t('subpage_rettindi_lifeyrisgatt_skyringar') }}</p>
              <h2 class="bold_text">{{ t('subpage_rettindi_lifeyrisgatt_header2') }}</h2>
              <p>{{ t('subpage_rettindi_lifeyrisgatt_fyrirvari') }}</p>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- CONSENT (before) -->
      <div v-else key="consent">
        <h2>{{ t('subpage_rettindi_lifeyrisgatt_samthykki_og_fyrivari_header') }}</h2>
        <p>{{ t('subpage_rettindi_lifeyrisgatt_samthykki_og_fyrivari') }}</p>

        <div class="m-b-30 agree">
          <v-checkbox
            v-model="confirm"
            color="primary"
            density="comfortable"
            :label="t('lan_umsokn_step7_confirm')"
            hide-details
          />
        </div>

        <Transition name="fade">
          <v-btn
            v-if="confirm"
            color="primary"
            class="forms__button forms__button__forward"
            @click="handleAccept"
          >
            {{ t('lan_umsokn_afram') }}
          </v-btn>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import { useJoakimStore } from '@/stores/useJoakimStore'
import type { DataTableHeader } from 'vuetify'

type LifeyrisgattItem = {
  adili: string
  vefSlod?: string
  tegund: 1 | 2
  sjodFelagi?: {
    rettindi?: {
      fyrirvari?: string
      manadargreidsla?: Array<{ aldur: number; einingar: number }>
    }
  }
  sidasGreitt?: string
}

const props = defineProps<{
  username: string
  gildiLatesPayment?: () => string | undefined
}>()
const emit = defineEmits<{ (e: 'fetchData'): void }>()

const lifeyrissjodir = ref<LifeyrisgattItem[]>([])
const confirm = ref(false)
const loading = ref(false)
const isAccepted = ref(localStorage.getItem('lifeyrisgatt') === 'true')

// pagination state (Vuetify-driven)
const page = ref(1)
const itemsPerPage = ref(5)
const pageSizes = [5, 10, 25, 50, 100]
const pageCount = computed(() => {
  const total = lifeyrissjodir.value.length || 0
  return Math.max(1, Math.ceil(total / itemsPerPage.value))
})
watch(itemsPerPage, () => { page.value = 1 })

const { t } = useI18n()
const store = useJoakimStore()

const personAge = computed(() => {
  const u = props.username || ''
  const day = u.substring(0, 2)
  const month = u.substring(2, 4)
  const yr = u.substring(4, 6)
  const birthday = `${month}-${day}-${yr}`
  const bday = moment(birthday).format('MM/DD/YYYY')
  const today = moment().format('MM/DD/YYYY')
  const diff = moment.duration(moment(today).diff(moment(bday)))
  return (diff as any)._data.years as number
})

const headers = computed<DataTableHeader[]>(() => ([
  { title: t('lifeyrisgattTable_adili'),          key: 'adili',          sortable: false },
  { title: t('lifeyrisgattTable_sidasGreitt'),    key: 'sidasGreitt',    sortable: false },
  { title: t('lifeyrisgattTable_tegund'),         key: 'tegund',         sortable: false },
  { title: t('lifeyrisgattTable_rettindi_one'),   key: 'rettindi_one',   align: 'end', sortable: false },
  { title: t('lifeyrisgattTable_rettindi_two'),   key: 'rettindi_two',   align: 'end', sortable: false },
  { title: t('lifeyrisgattTable_rettindi_three'), key: 'rettindi_three', align: 'end', sortable: false },
  { title: t('lifeyrisgattTable_lysing'),         key: 'lysing',         sortable: false },
]))

const visibleHeaders = computed<DataTableHeader[]>(() => {
  let list = headers.value.slice()
  if (personAge.value >= 65) list = list.filter(h => h.key !== 'rettindi_one')
  if (personAge.value >= 67) list = list.filter(h => h.key !== 'rettindi_two')
  return list
})

function formatHref(host?: string) {
  if (!host) return '#'
  return host.startsWith('http') ? host : `https://${host}`
}

function currency(n?: number | string | null) {
  if (n == null || n === '') return '0'
  const num = typeof n === 'string' ? Number(n) : n
  return new Intl.NumberFormat('is-IS', { maximumFractionDigits: 0 }).format(num || 0)
}

function checkAge(index: 0 | 1) {
  if (index === 0) return personAge.value < 65
  if (index === 1) return personAge.value < 67
  return true
}

function tegundLabel(tegund: number) {
  if (tegund === 1) return 'Krónur á mánuði'
  if (tegund === 2) return 'Hlutfall af launum'
  return ''
}

function getManadargreidslu(item: LifeyrisgattItem, idx: 0|1|2) {
  const ages = [65, 67, 70]
  const age = ages[idx]
  const mg = item?.sjodFelagi?.rettindi?.manadargreidsla ?? []
  const match = mg.find(m => m.aldur === age)
  if (!match) return 0
  if (item.tegund === 1) return match.einingar
  if (item.tegund === 2) return `${match.einingar} %`
  return 0
}

function getSumRettindi(idx: 0|1|2) {
  const ages = [65, 67, 70]
  const age = ages[idx]
  let sum = 0
  lifeyrissjodir.value.forEach(el => {
    const mg = el?.sjodFelagi?.rettindi?.manadargreidsla ?? []
    const match = mg.find(m => m.aldur === age)
    if (match && el.tegund === 1) sum += match.einingar || 0
  })
  return sum
}

async function fetchData() {
  loading.value = true
  try {
    const result = await store.getLifeyrisgatt()
    const payload = (result as any)?.data ?? result
    const items = payload?.lifeyrir ?? payload ?? []
    lifeyrissjodir.value = items

    if (props.gildiLatesPayment) {
      const g = lifeyrissjodir.value.find(x => x.adili === 'Gildi - lífeyrissjóður')
      if (g) g.sidasGreitt = props.gildiLatesPayment()
    }
  } finally {
    loading.value = false
  }
}

function handleAccept() {
  if (!confirm.value) return
  localStorage.setItem('lifeyrisgatt', 'true')
  isAccepted.value = true
  fetchData()
  emit('fetchData')
}

onMounted(async () => {
  if (isAccepted.value) await fetchData()
})
</script>

<style scoped lang="scss">
.bold_text { font-size: 26px !important; }

.lifeyr-table :deep(th[role="columnheader"]) {
  white-space: nowrap;
}

.table_link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #194458;
  text-decoration: none;
  font-weight: 600;
}
.table_link:hover { text-decoration: underline; }
.table_link .ext { width: 16px; height: 16px; fill: currentColor; opacity: .6; }

/* ✅ summary row inside table matches column widths */
.lifeyr-table :deep(tbody tr.summary-row) td {
  padding: 12px 16px;
  border-top: 1px solid rgba(0,0,0,.12);
}
.lifeyr-table :deep(tbody tr.summary-row .label) { font-weight: 700; }
.lifeyr-table :deep(tbody tr.summary-row td.end) { text-align: right; font-weight: 700; }

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 4px 16px;
}
.rows-select { max-width: 160px; }
.aftertext { margin-top: 24px; }

.fade-enter-active, .fade-leave-active { transition: opacity .25s ease; }
.fade-enter-from,  .fade-leave-to      { opacity: 0; }
.agree { margin-top: 12px; }
</style>